/*============================================================================
  Cart drawer - Shipping protection toggle

  Three things decide how this behaves:

  1. The toggle answers immediately. A click flips it on the spot and the
     request goes out behind it - the shopper never waits on the network to see
     their own tap register. Rapid taps collapse into the fewest writes needed
     to reach the state they settled on.

  2. Toggling never re-renders the cart. The protection's line is hidden in the
     drawer, so the only thing its refresh changes is the total - but the
     theme's refresh re-inserts every line item, and those fade in from
     opacity 0, which reads as the whole drawer blinking. The freshly inserted
     rows are stamped to skip that animation, so the total updates and nothing
     else moves.

  3. The protection moves with the cart in the same write, not after it:

       - Adding a product   `theme:cart:add` fires on click, ahead of the
                            theme's own request, so the theme's single refresh
                            already has the protection in it.

       - Emptying the cart  the remove button and the quantity stepper are
                            caught in the capture phase, before the theme's
                            handlers on the line item, so the last product and
                            the protection go in one clear - the drawer never
                            paints a cart holding only the protection.

  Everything still settles against `theme:cart:change`, so if a shortcut is
  missed the reactive path is the fallback and the cart stays correct.
==============================================================================*/

(function () {
  const selectors = {
    drawer: 'cart-drawer',
    lineItem: '.cart__item',
    protectionLineItem: '.cart__item[data-shipping-protection]',
    toggle: '[data-protection-toggle]',
    itemRemove: '[data-item-remove]',
    animated: '[data-animation]',
    itemsHolder: '[data-items-holder]',
    priceHolder: '[data-cart-price-holder]',
  };

  const classes = {
    on: 'is-on',
    loading: 'is-loading',
    removed: 'is-removed',
    noAnimation: 'shipping-protection-no-animation',
  };

  const attributes = {
    protection: 'data-shipping-protection',
  };

  // How long to let the theme's own refresh arrive before forcing one. Only
  // reached when the shopper's add failed, or nothing refreshed the cart.
  const RENDER_GRACE = 1500;

  // Remembers an opt-out for the tab, so turning it off does not get undone by
  // the next auto-add. Cleared as soon as the shopper turns it back on.
  const OPT_OUT_KEY = 'theme:shipping-protection:opted-out';

  const readOptOut = () => {
    try {
      return window.sessionStorage.getItem(OPT_OUT_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  const writeOptOut = (value) => {
    try {
      if (value) {
        window.sessionStorage.setItem(OPT_OUT_KEY, 'true');
      } else {
        window.sessionStorage.removeItem(OPT_OUT_KEY);
      }
    } catch (error) {
      // Private browsing - the toggle still works, it just will not be remembered.
    }
  };

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`${url} responded ${response.status}`);
      return response.json();
    });

  class CartShippingProtection extends HTMLElement {
    constructor() {
      super();

      this.variantId = Number(this.dataset.variantId);
      this.defaultOn = this.dataset.defaultOn === 'true';

      // What the cart is known to hold.
      this.inCart = this.dataset.inCart === 'true';
      this.otherItems = Number(this.dataset.otherItems) || 0;

      // What the shopper has asked for but the cart has not caught up with yet.
      // This is what the toggle shows, so it always reflects their last tap.
      this.desired = null;

      this.writing = false;
      this.awaitingRender = false;
      this.silentRefresh = false;
      this.autoAddFailed = false;

      // Counts drawer paints, so a write can tell whether one landed while it
      // was in flight. See runWrite.
      this.renderSeq = 0;

      this.onToggleClick = this.onToggleClick.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      this.onDrawerOpen = this.onDrawerOpen.bind(this);
      this.onCartAdd = this.onCartAdd.bind(this);
      this.onRemoveCapture = this.onRemoveCapture.bind(this);
      this.onQuantityCapture = this.onQuantityCapture.bind(this);
    }

    connectedCallback() {
      // Looked up here rather than in the constructor: an element upgraded while
      // the document is still parsing has no children yet.
      this.toggle = this.querySelector(selectors.toggle);
      this.drawer = this.closest(selectors.drawer);

      if (!this.variantId || !this.toggle) return;

      this.toggle.addEventListener('click', this.onToggleClick);
      document.addEventListener('theme:cart:change', this.onCartChange);
      document.addEventListener('theme:cart-drawer:open', this.onDrawerOpen);
      document.addEventListener('theme:cart:add', this.onCartAdd);

      // Capture phase on the drawer, so these run before the theme's own
      // listeners further down on the line item.
      if (this.drawer) {
        this.drawer.addEventListener('click', this.onRemoveCapture, true);
        this.drawer.addEventListener('theme:cart:update', this.onQuantityCapture, true);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.renderTimer);

      this.toggle?.removeEventListener('click', this.onToggleClick);
      document.removeEventListener('theme:cart:change', this.onCartChange);
      document.removeEventListener('theme:cart-drawer:open', this.onDrawerOpen);
      document.removeEventListener('theme:cart:add', this.onCartAdd);

      this.drawer?.removeEventListener('click', this.onRemoveCapture, true);
      this.drawer?.removeEventListener('theme:cart:update', this.onQuantityCapture, true);
    }

    /*====== State ======*/

    /** What the toggle shows: the shopper's intent first, the cart second. */
    get shown() {
      return this.desired === null ? this.inCart : this.desired;
    }

    get settled() {
      return !this.writing && this.desired === null && !this.awaitingRender;
    }

    /**
     * Re-read the cart from the line items the theme has rendered.
     *
     * The product count always comes from the DOM - the theme keeps it honest.
     * The protection only does once nothing of ours is in flight, otherwise a
     * paint that predates our write would flip the toggle back under the
     * shopper's finger.
     *
     * @return {Void}
     */
    readCartState() {
      const scope = this.drawer || document;
      const lineItems = scope.querySelectorAll(selectors.lineItem);

      this.otherItems = Array.from(lineItems).filter((item) => !item.hasAttribute(attributes.protection)).length;

      if (this.settled) {
        this.inCart = Boolean(scope.querySelector(selectors.protectionLineItem));
      }
    }

    render() {
      const shown = this.shown;

      this.classList.toggle(classes.on, shown);
      this.toggle.setAttribute('aria-checked', String(shown));
      this.dataset.inCart = String(shown);
      this.dataset.otherItems = String(this.otherItems);
    }

    /*====== Events ======*/

    /**
     * Flip now, reconcile after. The toggle is never blocked on a request.
     *
     * @return {Void}
     */
    onToggleClick(event) {
      event.preventDefault();

      const next = !this.shown;

      writeOptOut(!next);
      if (next) this.autoAddFailed = false;

      this.request(next);
    }

    /**
     * The shopper has just clicked an "Add to cart" button. This fires before
     * the theme sends its own request, so adding the protection now puts it in
     * the cart in time for the theme's single refresh to include it - no second
     * render, and the drawer opens with the toggle already on.
     *
     * @return {Void}
     */
    onCartAdd() {
      if (!this.defaultOn || this.autoAddFailed) return;
      if (readOptOut()) return;
      if (this.shown) return;

      this.request(true, {eager: true});
    }

    onRemoveCapture(event) {
      const button = event.target.closest?.(selectors.itemRemove);
      if (!button) return;

      this.interceptLastItem(event, button.closest(selectors.lineItem), 0);
    }

    onQuantityCapture(event) {
      const item = event.target.closest?.(selectors.lineItem);

      this.interceptLastItem(event, item, Number(event.detail?.quantity));
    }

    /**
     * When the shopper is removing the only thing the protection was protecting,
     * empty the cart in one write rather than letting the theme render a cart
     * holding nothing but the protection and then tidying up after it.
     *
     * @param   {Event}  event
     * @param   {HTMLElement}  item  The line item being changed
     * @param   {Number}  nextQuantity  What its quantity is about to become
     *
     * @return  {Void}
     */
    interceptLastItem(event, item, nextQuantity) {
      if (!item || nextQuantity !== 0) return;
      if (item.hasAttribute(attributes.protection)) return;

      this.readCartState();

      // Only when the protection is on and this is the last real line.
      if (!this.shown || this.otherItems > 1) return;

      event.preventDefault();
      event.stopPropagation();

      // Mirror the theme's own removal feedback so it looks like any other.
      item.classList.add(classes.removed);
      this.drawer?.classList.add(classes.loading);

      this.clearCart();
    }

    onCartChange() {
      this.renderSeq += 1;

      if (this.silentRefresh) {
        this.silentRefresh = false;
        this.skipAnimationOnFreshRows();
      }

      this.readCartState();
      this.render();

      // Fallback for a cart emptied somewhere the capture handlers do not reach.
      if (this.inCart && this.otherItems === 0 && this.settled) {
        this.clearCart();
        return;
      }

      if (this.awaitingRender) {
        this.awaitingRender = false;
        clearTimeout(this.renderTimer);

        // The theme painted before the eager add landed, so bring it in now.
        if (!this.inCart) this.refreshCart();
        return;
      }

      this.maybeAutoAdd();
    }

    onDrawerOpen() {
      this.readCartState();
      this.render();
      this.maybeAutoAdd();
    }

    /**
     * Catch-all for carts that arrived without passing through `theme:cart:add`
     * - a cart carried over from an earlier visit, for one.
     *
     * @return {Void}
     */
    maybeAutoAdd() {
      if (!this.defaultOn || this.autoAddFailed) return;
      if (!this.settled) return;
      if (this.shown || this.otherItems === 0) return;
      if (readOptOut()) return;

      this.request(true);
    }

    /*====== Writes ======*/

    /**
     * Record what the shopper wants, show it straight away, then reconcile.
     *
     * @param   {Boolean}  state
     * @param   {Boolean}  eager  Riding along with the shopper's own add, so the
     *                            theme is already refreshing - do not ask for a
     *                            second one, settle against that render instead.
     *
     * @return  {Void}
     */
    request(state, {eager = false} = {}) {
      this.desired = state;
      this.eager = eager;
      this.render();
      this.flush();
    }

    /**
     * Send whatever is needed to reach the desired state. Only one request is in
     * flight at a time, so a flurry of taps collapses to the last one that
     * actually differs from the cart.
     *
     * @return {Void}
     */
    flush() {
      if (this.writing || this.desired === null) return;

      const target = this.desired;

      // Already there - nothing to send.
      if (target === this.inCart) {
        this.desired = null;
        this.render();
        return;
      }

      this.writing = true;

      const seqAtWrite = this.renderSeq;
      const eager = this.eager;
      const write = target
        ? postJSON(window.theme.routes.cart_add_url, {items: [{id: this.variantId, quantity: 1}]})
        : postJSON(window.theme.routes.cart_update_url, {updates: {[this.variantId]: 0}});

      write
        .then(() => {
          this.writing = false;
          this.inCart = target;

          if (this.desired === target) this.desired = null;

          // A newer tap came in while this was in flight - go again.
          if (this.desired !== null) {
            this.flush();
            return;
          }

          this.eager = false;
          this.settleAfterWrite(target, seqAtWrite, eager);
        })
        .catch((error) => {
          console.error('Shipping protection:', error);

          this.writing = false;
          this.desired = null;
          this.eager = false;

          // Stop retrying on every cart change once the product has refused to
          // be added - a sold out or unpublished product would otherwise fire a
          // request on each update.
          if (target) this.autoAddFailed = true;

          this.render();
        });
    }

    /**
     * @param   {Boolean}  target      The state just written
     * @param   {Number}   seqAtWrite  Paint count when the write went out
     * @param   {Boolean}  eager       Whether the theme is already refreshing
     *
     * @return  {Void}
     */
    settleAfterWrite(target, seqAtWrite, eager) {
      if (!eager) {
        this.refreshCart();
        return;
      }

      if (this.renderSeq > seqAtWrite) {
        // The theme has already painted since this went out. Either it caught
        // the protection - nothing to do - or it beat it, in which case bring it
        // in now rather than sitting out of sync until the grace timer fires.
        const rendered = Boolean((this.drawer || document).querySelector(selectors.protectionLineItem));

        if (rendered !== target) this.refreshCart();
        return;
      }

      // Nothing painted yet, so the refresh already on its way should carry it.
      this.awaitingRender = true;
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => {
        if (!this.awaitingRender) return;

        // No render arrived - the shopper's own add errored, most likely.
        this.awaitingRender = false;
        this.refreshCart();
      }, RENDER_GRACE);
    }

    /**
     * Empties the cart in a single request, so the drawer goes straight to its
     * empty state instead of stopping at a cart holding only the protection.
     *
     * @return {Void}
     */
    clearCart() {
      this.writing = true;
      this.desired = null;

      const cartUrl = (window.theme.routes.cart_url || '/cart').replace(/\/$/, '');

      postJSON(`${cartUrl}/clear.js`, {})
        .then(() => {
          this.writing = false;
          this.inCart = false;
          this.refreshCart();
        })
        .catch((error) => {
          console.error('Shipping protection:', error);
          this.writing = false;
          this.drawer?.classList.remove(classes.loading);
          this.render();
        });
    }

    /*====== Rendering ======*/

    /**
     * Rows the theme is about to fade in get marked to appear at once instead.
     *
     * Only the contents of the line item and totals holders are touched, and
     * those are replaced wholesale on every refresh - so the marker leaves with
     * them and an ordinary cart change animates as it always did.
     *
     * @return {Void}
     */
    skipAnimationOnFreshRows() {
      const scope = this.drawer;
      if (!scope) return;

      [selectors.itemsHolder, selectors.priceHolder].forEach((holder) => {
        scope
          .querySelector(holder)
          ?.querySelectorAll(selectors.animated)
          .forEach((row) => row.classList.add(classes.noAnimation));
      });
    }

    /**
     * Hand back to the theme, which re-renders the line items and the totals and
     * then dispatches `theme:cart:change` - picked up by onCartChange above.
     * Flagged as ours so that render lands without the fade.
     *
     * @return {Void}
     */
    refreshCart() {
      this.silentRefresh = true;
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }
  }

  if (!customElements.get('cart-shipping-protection')) {
    customElements.define('cart-shipping-protection', CartShippingProtection);
  }
})();
